import { useState, useEffect, useRef, useCallback } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { enqueueOfflineMessage, getQueue, removeFromQueue } from '../utils/offlineMessageQueue';
import { trackEvent } from '../utils/tracking';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Send,
  Loader2,
  Check,
  CheckCheck,
  Wifi,
  WifiOff,
  Bell,
  BellOff,
  Image as ImageIcon,
  MessageSquare,
  Search,
  X,
  Flag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import { ReportUserModal } from '../components/ReportUserModal';
import { ConductWarningModal } from '../components/ConductWarningModal';
import { PreAuthPanel } from '../components/PreAuthPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Push notification utilities
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return false;
};

const showNotification = (title, body) => {
  if (Notification.permission === 'granted' && document.hidden) {
    const notification = new Notification(title, {
      body,
      icon: '/logo-wantd-dark.png',
      tag: 'wantd-chat',
      renotify: true
    });
    notification.onclick = () => { window.focus(); notification.close(); };
    setTimeout(() => notification.close(), 5000);
  }
};

export default function MessagesPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, token, api, registerWebPushToken } = useAuth();
  const { isOnline, justCameOnline } = useOnlineStatus();

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Active chat state
  const [activeMatchId, setActiveMatchId] = useState(matchId || null);

  // Flush offline message queue when we come back online
  useEffect(() => {
    if (!justCameOnline || !activeMatchId) return;
    const queue = getQueue().filter((m) => m.matchId === activeMatchId);
    if (!queue.length) return;
    (async () => {
      for (const entry of queue) {
        try {
          const response = await api.post('/messages/', {
            match_id: entry.matchId,
            content: entry.content,
          });
          setMessages((prev) => [...prev, response.data]);
          removeFromQueue(entry.queuedAt);
        } catch {
          // Leave in queue for next reconnect
        }
      }
      if (queue.length) toast.success('Queued messages sent');
    })();
  }, [justCameOnline, activeMatchId]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [matchInfo, setMatchInfo] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isOtherOnline, setIsOtherOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [usePolling, setUsePolling] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showConductWarning, setShowConductWarning] = useState(false);
  const conductWarningShown = useRef(false);

  // Conversation filter
  const [convFilter, setConvFilter] = useState('all'); // 'all' | 'inbound' | 'outbound'

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [api]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Refetch conversations when we navigate to a new match (e.g., from start-conversation)
  useEffect(() => {
    if (activeMatchId && !conversations.find(c => c.match_id === activeMatchId)) {
      // New conversation not in list, refetch
      fetchConversations();
    }
  }, [activeMatchId, conversations, fetchConversations]);

  // URL-based active match selection
  useEffect(() => {
    if (matchId) {
      setActiveMatchId(matchId);
    }
  }, [matchId]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Fetch match info for active chat
  const fetchMatchInfo = useCallback(async (mId) => {
    if (!mId) return;
    try {
      const response = await api.get(`/matches/${mId}`);
      setMatchInfo(response.data);
      const isWanter = response.data.wanter_id === user?.id;
      setOtherUser({
        id: isWanter ? response.data.owner_id : response.data.wanter_id,
        username: isWanter ? response.data.owner_username : response.data.wanter_username
      });
    } catch (error) {
      console.error('Failed to fetch match:', error);
    }
  }, [api, user?.id]);

  // Fetch messages for active chat
  const fetchMessages = useCallback(async (mId) => {
    if (!mId) return;
    try {
      const response = await api.get(`/messages/${mId}`);
      setMessages(prev => {
        if (response.data.length > prev.length) {
          const newMsgs = response.data.slice(prev.length);
          newMsgs.forEach(msg => {
            if (msg.sender_id !== user?.id && notificationsEnabled) {
              showNotification(`New message from @${msg.sender_username}`, msg.content);
            }
          });
        }
        return response.data;
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [api, user?.id, notificationsEnabled]);

  // Connect WebSocket
  const connectWebSocket = useCallback((mId) => {
    if (!mId || !token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    
    reconnectAttemptsRef.current = 0;
    
    const tryConnect = () => {
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        setUsePolling(true);
        return;
      }

      const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
      const wsHost = API_URL.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}://${wsHost}/api/ws/chat/${mId}?token=${token}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          setIsConnected(true);
          setUsePolling(false);
          reconnectAttemptsRef.current = 0;
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        };

        wsRef.current.onerror = () => {
          reconnectAttemptsRef.current++;
        };

        wsRef.current.onclose = (event) => {
          setIsConnected(false);
          setIsOtherOnline(false);
          
          if ([4001, 4003, 4004, 403].includes(event.code)) {
            setUsePolling(true);
            return;
          }
          
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current++;
            setTimeout(tryConnect, 2000);
          } else {
            setUsePolling(true);
          }
        };
      } catch (error) {
        setUsePolling(true);
      }
    };
    
    tryConnect();
  }, [token]);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        setIsOtherOnline(data.other_online);
        break;
      case 'message':
        setMessages(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          if (data.sender_id !== user?.id && notificationsEnabled) {
            showNotification(`New message from @${data.sender_username}`, data.content);
          }
          return [...prev, {
            id: data.id,
            sender_id: data.sender_id,
            sender_username: data.sender_username,
            content: data.content,
            created_at: data.created_at,
            is_read: true
          }];
        });
        break;
      case 'user_online':
        setIsOtherOnline(true);
        break;
      case 'user_offline':
        setIsOtherOnline(false);
        break;
      case 'typing':
        setIsTyping(data.is_typing);
        break;
      case 'messages_read':
        setMessages(prev => prev.map(m =>
          m.sender_id === user?.id ? { ...m, is_read: true } : m
        ));
        break;
      case 'error':
        if (data.error === 'blocked_link') {
          toast.error('This message contains a link flagged as unsafe and was not sent.');
        }
        break;
      default:
        break;
    }
  }, [user?.id, notificationsEnabled]);

  // Load chat when active match changes
  useEffect(() => {
    if (activeMatchId && token) {
      setLoadingMessages(true);
      setMessages([]);
      setMatchInfo(null);
      setOtherUser(null);
      setIsConnected(false);
      setUsePolling(false);
      
      fetchMatchInfo(activeMatchId);
      fetchMessages(activeMatchId);
      connectWebSocket(activeMatchId);

      // Show conduct warning on first chat open
      if (!user?.has_seen_messaging_warning && !conductWarningShown.current) {
        conductWarningShown.current = true;
        setShowConductWarning(true);
      }
      
      // Update URL without navigation
      if (matchId !== activeMatchId) {
        navigate(`/messages/${activeMatchId}`, { replace: true });
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [activeMatchId, token]);

  // Polling fallback
  useEffect(() => {
    if (usePolling && !isConnected && activeMatchId) {
      pollingIntervalRef.current = setInterval(() => fetchMessages(activeMatchId), 5000);
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [usePolling, isConnected, activeMatchId, fetchMessages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !activeMatchId) return;

    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Queue message if offline
    if (!isOnline) {
      enqueueOfflineMessage(activeMatchId, content);
      // Optimistically show in UI with a pending indicator
      setMessages((prev) => [
        ...prev,
        {
          id:         `queued-${Date.now()}`,
          content,
          sender_id:  user?.id,
          created_at: new Date().toISOString(),
          queued:     true,
        },
      ]);
      toast('Message queued — will send when you reconnect', { icon: '📶' });
      setSending(false);
      return;
    }

    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message', content }));
      } else {
        const response = await api.post('/messages/', {
          match_id: activeMatchId,
          content
        });
        setMessages(prev => [...prev, response.data]);
      }
      trackEvent('message', { item_id: activeMatchId });

      // Track message event (non-blocking, item context if available)
      trackEvent('message', {
        item_id: matchInfo?.inventory_item_id || undefined,
      });

      // Update conversations list with new message
      setConversations(prev => prev.map(c =>
        c.match_id === activeMatchId
          ? { ...c, last_message: { content, sender_id: user?.id, created_at: new Date().toISOString() } }
          : c
      ));
    } catch (error) {
      const errCode = error.response?.data?.detail?.error;
      if (errCode === 'geo_restricted') {
        toast.error('WANTD is currently available in Australia only.');
      } else if (errCode === 'blocked_link') {
        toast.error('This message contains a link flagged as unsafe and was not sent.');
      } else if (errCode === 'account_suspended') {
        toast.error(error.response?.data?.detail?.message || 'Your account is suspended.');
      } else if (errCode === 'account_banned') {
        toast.error('Your account has been permanently banned.');
      } else {
        toast.error('Failed to send message');
      }
      setNewMessage(content);
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      }, 2000);
    }
  };

  const toggleNotifications = async () => {
    if (notificationsEnabled) {
      setNotificationsEnabled(false);
      toast.success('Notifications disabled');
    } else {
      const granted = await requestNotificationPermission();
      if (granted) {
        setNotificationsEnabled(true);
        toast.success('Notifications enabled!');
        // Register FCM web push token now that permission is granted
        registerWebPushToken?.();
      } else {
        toast.error('Please enable notifications in browser settings');
      }
    }
  };

  // Helper to determine user roles
  const isWanter = matchInfo?.wanter_id === user?.id;
  const isOwner = matchInfo?.owner_id === user?.id;

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    // Handle both string and object formats
    if (typeof img === 'object' && img.url) return img.url;
    if (typeof img !== 'string') return null;
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  // Filter conversations by search + direction
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = !searchQuery ||
      conv.other_username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.item_title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDirection = convFilter === 'all' || conv.direction === convFilter;
    return matchesSearch && matchesDirection;
  });

  // Get item images from match
  const wanterImage = matchInfo?.wtb_images?.[0];
  const ownerImage = matchInfo?.inventory_images?.[0];

  return (
    <Layout hideFooter>
      <div className="fixed inset-0 top-16 md:top-[72px] flex overflow-hidden bg-[var(--theme-bg)]">
        {/* Conversations Sidebar */}
        <div className={`${activeMatchId ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r border-[var(--theme-border)] bg-[var(--theme-card)]`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-[var(--theme-border)]">
            <h1 className="text-xl font-bold text-[var(--theme-text-primary)] mb-3">Messages</h1>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--theme-text-muted)]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="pl-9 bg-[var(--theme-bg)] border-[var(--theme-border)] rounded-full h-9 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-[var(--theme-text-muted)]" />
                </button>
              )}
            </div>

            {/* Direction Filter Tabs */}
            <div className="flex gap-1 mt-2.5">
              {[
                { key: 'all', label: 'All' },
                { key: 'inbound', label: 'Selling' },
                { key: 'outbound', label: 'Buying' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setConvFilter(tab.key)}
                  className={`flex-1 py-1 px-2 rounded-full text-xs font-medium transition-colors ${
                    convFilter === tab.key
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loadingConversations ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-[var(--theme-text-muted)] opacity-50" />
                <p className="text-[var(--theme-text-muted)]">
                  {searchQuery ? 'No conversations found' : 'No conversations yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--theme-border)]">
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.match_id}
                    onClick={() => setActiveMatchId(conv.match_id)}
                    className={`w-full text-left p-4 hover:bg-[var(--theme-hover)] transition-colors ${
                      activeMatchId === conv.match_id ? 'bg-[var(--theme-hover)]' : ''
                    }`}
                    data-testid={`conversation-${conv.match_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-bold">
                          {conv.other_username?.[0]?.toUpperCase() || 'U'}
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#C9956C] text-white text-xs flex items-center justify-center font-bold">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`font-semibold text-sm truncate ${conv.unread_count > 0 ? 'text-[var(--theme-text-primary)]' : 'text-[var(--theme-text-secondary)]'}`}>
                            {conv.item_title}
                          </span>
                          <span className="text-xs text-[var(--theme-text-muted)] flex-shrink-0 ml-2">
                            {formatRelativeTime(conv.last_message?.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--theme-text-muted)] truncate mb-1">@{conv.other_username}</p>
                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-[var(--theme-text-primary)] font-medium' : 'text-[var(--theme-text-muted)]'}`}>
                          {conv.last_message?.sender_id === user?.id && 'You: '}
                          {conv.last_message?.content || 'No messages yet'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${activeMatchId ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-[var(--theme-bg)] overflow-hidden`}>
          {activeMatchId ? (
            <>
              {/* Chat Header - Sticky */}
              <div className="flex-shrink-0 bg-[var(--theme-card)] border-b border-[var(--theme-border)] shadow-sm">
                {/* User Row */}
                <div className="px-4 py-3 flex items-center gap-3">
                  <button 
                    onClick={() => {
                      setActiveMatchId(null);
                      navigate('/messages', { replace: true });
                    }} 
                    className="md:hidden p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors"
                    data-testid="chat-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                  </button>
                  
                  <div className="flex items-center gap-3 flex-1">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-bold">
                        {otherUser?.username?.[0]?.toUpperCase() || 'U'}
                      </div>
                      {isConnected && (
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[var(--theme-card)] ${isOtherOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                      )}
                    </div>
                    <div>
                      <h2 className="font-bold text-[var(--theme-text-primary)]" data-testid="chat-username">
                        {otherUser?.id ? (
                          <Link
                            to={`/user/${otherUser.id}`}
                            className="hover:text-[#C9956C] transition-colors"
                          >
                            @{otherUser?.username}
                          </Link>
                        ) : (
                          <>@{otherUser?.username}</>
                        )}
                      </h2>
                      <p className="text-xs text-[var(--theme-text-secondary)]">
                        {isTyping ? (
                          <span className="text-[#C9956C]">Typing...</span>
                        ) : isOtherOnline ? (
                          <span className="text-green-500">Online</span>
                        ) : (
                          <span>Offline</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowReportModal(true)}
                    className="p-2 rounded-full text-[var(--theme-text-muted)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    title="Report user"
                    data-testid="report-user-btn"
                  >
                    <Flag className="w-5 h-5" />
                  </button>

                  <button
                    onClick={toggleNotifications}
                    className={`p-2 rounded-full transition-colors ${notificationsEnabled ? 'text-[#C9956C] hover:bg-[#C9956C]/10' : 'text-[var(--theme-text-muted)] hover:bg-[var(--theme-hover)]'}`}
                    title={notificationsEnabled ? 'Notifications enabled' : 'Enable notifications'}
                    data-testid="notification-toggle"
                  >
                    {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                  </button>

                  <div className="flex items-center gap-1 group relative">
                    {isConnected ? (
                      <div className="flex items-center gap-1 text-green-500 text-xs" title="Real-time connection active — messages appear instantly">
                        <Wifi className="w-4 h-4" />
                        <span className="hidden group-hover:block absolute right-0 top-full mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg px-2.5 py-1.5 text-[11px] text-[var(--theme-text-secondary)] whitespace-nowrap shadow-lg z-50">Live connection — messages appear instantly</span>
                      </div>
                    ) : usePolling ? (
                      <div className="flex items-center gap-1 text-[var(--theme-text-muted)] text-xs" title="Live connection unavailable — checking for new messages periodically">
                        <WifiOff className="w-4 h-4" />
                        <span className="hidden group-hover:block absolute right-0 top-full mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg px-2.5 py-1.5 text-[11px] text-[var(--theme-text-secondary)] whitespace-nowrap shadow-lg z-50">Checking for new messages periodically</span>
                      </div>
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin text-[var(--theme-text-muted)]" />
                    )}
                  </div>
                </div>

                {/* Side-by-side Item Context */}
                {matchInfo && (
                  <div className="px-4 pb-3">
                    <div className="p-3 rounded-xl bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                      {/* Show both items only if this is a WTB match (has wtb_listing_id) */}
                      {matchInfo.wtb_listing_id && matchInfo.inventory_item_id ? (
                        <div className="grid grid-cols-2 gap-3">
                          {/* Want Item */}
                          <div className="flex items-center gap-2">
                            <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-[var(--theme-card)] border border-[#C9956C]/30 flex-shrink-0">
                              {wanterImage ? (
                                <img src={getImageUrl(wanterImage)} alt="Want" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-[#C9956C]/50">
                                  <ImageIcon className="w-5 h-5" />
                                </div>
                              )}
                              <span className="absolute bottom-0 left-0 right-0 bg-[#C9956C] text-white text-[7px] font-bold text-center py-0.5">WANT</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[9px] uppercase tracking-wide text-[#C9956C] font-semibold">
                                {isWanter ? 'Your Want' : `@${matchInfo.wanter_username}`}
                              </p>
                              <Link to={`/wtb/${matchInfo.wtb_listing_id}`} className="text-xs font-medium text-[var(--theme-text-primary)] truncate hover:text-[#C9956C] transition-colors block">{matchInfo.wtb_title}</Link>
                            </div>
                          </div>

                          {/* Inventory Item */}
                          <div className="flex items-center gap-2">
                            <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-[var(--theme-card)] border border-[#1E3A5F]/30 flex-shrink-0">
                              {ownerImage ? (
                                <img src={getImageUrl(ownerImage)} alt="Have" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-[#1E3A5F]/50">
                                  <ImageIcon className="w-5 h-5" />
                                </div>
                              )}
                              <span className="absolute bottom-0 left-0 right-0 bg-[#1E3A5F] text-white text-[7px] font-bold text-center py-0.5">HAVE</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[9px] uppercase tracking-wide text-[#1E3A5F] font-semibold">
                                {!isWanter ? 'Your Item' : `@${matchInfo.owner_username}`}
                              </p>
                              <Link to={`/catalog/${matchInfo.inventory_item_id}`} className="text-xs font-medium text-[var(--theme-text-primary)] truncate hover:text-[#1E3A5F] transition-colors block">{matchInfo.inventory_title}</Link>
                            </div>
                          </div>
                        </div>
                      ) : matchInfo.inventory_item_id ? (
                        /* Direct inquiry about an item - show only the item */
                        <div className="flex items-center gap-3">
                          <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-[var(--theme-card)] border border-[#1E3A5F]/30 flex-shrink-0">
                            {ownerImage ? (
                              <img src={getImageUrl(ownerImage)} alt="Item" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#1E3A5F]/50">
                                <ImageIcon className="w-6 h-6" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase tracking-wide text-[#1E3A5F] font-semibold">
                              {isOwner ? 'Your Item' : 'Item for Sale'}
                            </p>
                            <Link to={`/catalog/${matchInfo.inventory_item_id}`} className="text-sm font-medium text-[var(--theme-text-primary)] truncate hover:text-[#1E3A5F] transition-colors block">{matchInfo.inventory_title}</Link>
                            <p className="text-xs text-[var(--theme-text-muted)]">
                              {isOwner ? `Buyer: @${matchInfo.wanter_username}` : `Seller: @${matchInfo.owner_username}`}
                            </p>
                          </div>
                        </div>
                      ) : matchInfo.wtb_listing_id ? (
                        /* Direct inquiry about a want - show only the want */
                        <div className="flex items-center gap-3">
                          <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-[var(--theme-card)] border border-[#C9956C]/30 flex-shrink-0">
                            {wanterImage ? (
                              <img src={getImageUrl(wanterImage)} alt="Want" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#C9956C]/50">
                                <ImageIcon className="w-6 h-6" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase tracking-wide text-[#C9956C] font-semibold">
                              {isWanter ? 'Your Want' : 'Want Listing'}
                            </p>
                            <p className="text-sm font-medium text-[var(--theme-text-primary)] truncate">{matchInfo.wtb_title}</p>
                            <p className="text-xs text-[var(--theme-text-muted)]">
                              {isWanter ? `Seller: @${matchInfo.owner_username}` : `Buyer: @${matchInfo.wanter_username}`}
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>

              {/* Pre-auth security hold panel */}
              <PreAuthPanel
                matchId={activeMatchId}
                matchInfo={matchInfo}
                currentUser={user}
                api={api}
              />

              {/* Messages Area - Scrollable */}
              <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="chat-messages">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                      <div key={date}>
                        <div className="flex items-center justify-center mb-4">
                          <span className="px-3 py-1 rounded-full bg-[var(--theme-hover)] text-xs text-[var(--theme-text-muted)]">
                            {date}
                          </span>
                        </div>
                        {dateMessages.map((message, index) => {
                          const isOwn = message.sender_id === user?.id;
                          return (
                            <div key={message.id || index} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
                              <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                                isOwn 
                                  ? 'bg-[#1E3A5F] text-white rounded-br-md' 
                                  : 'bg-[var(--theme-hover)] text-[var(--theme-text-primary)] rounded-bl-md'
                              }`}>
                                <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                                <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                  <span className={`text-[10px] ${isOwn ? 'text-white/60' : 'text-[var(--theme-text-muted)]'}`}>
                                    {formatTime(message.created_at)}
                                  </span>
                                  {isOwn && (
                                    message.is_read ? (
                                      <CheckCheck className="w-3 h-3 text-[#4ADE80]" />
                                    ) : (
                                      <Check className="w-3 h-3 text-white/60" />
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}

                    {isTyping && (
                      <div className="flex justify-start mb-2">
                        <div className="bg-[var(--theme-hover)] rounded-2xl px-4 py-2 rounded-bl-md">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 rounded-full bg-[var(--theme-text-muted)] animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 rounded-full bg-[var(--theme-text-muted)] animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 rounded-full bg-[var(--theme-text-muted)] animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input - Fixed at bottom */}
              <div className="flex-shrink-0 px-4 py-3 border-t border-[var(--theme-border)] bg-[var(--theme-card)]">
                <div className="flex gap-2">
                  <form onSubmit={sendMessage} className="flex gap-2 flex-1">
                    <Input
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message..."
                      className="flex-1 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] rounded-full px-4"
                      data-testid="chat-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="rounded-full w-10 h-10 p-0 bg-[#1E3A5F] hover:bg-[#2D4A6F]"
                      data-testid="chat-send-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </form>
                </div>
              </div>
            </>
          ) : (
            /* Empty State - No chat selected */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center p-8">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-[var(--theme-text-muted)] opacity-30" />
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Select a conversation</h3>
                <p className="text-[var(--theme-text-muted)]">Choose a conversation from the left to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ReportUserModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportedUserId={otherUser?.id}
        reportedUsername={otherUser?.username}
      />

      <ConductWarningModal
        isOpen={showConductWarning}
        onClose={() => setShowConductWarning(false)}
      />
    </Layout>
  );
}
